import { FC, useMemo } from 'react';
import { FeatureTrialData } from './types';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import {
  Heading,
  BlockIconSmall,
  ClockIconSmall,
  PrimaryButton,
  Text,
  useModalControl,
  Chip,
} from '@weave/design-system';
import { FeatureEnumToNameMap } from 'constants/feature-data';
import { SubscriptionAddOnStatus } from '@weave/schema-gen-ts/dist/schemas/subscription-manager-service/v1/subscription_manager_service.pb';
import { getFeatureTrialDuration } from './utils';
import { StartTrialModal } from './start-trial-modal';

const ENDING_SOON_THRESHOLD_DAYS = 7;

export const TrialCard: FC<FeatureTrialData> = ({
  featureEnum,
  addonEnum,
  addonStatus,
  trialEndDate,
  daysLeftInTrial = 0,
  trialExpiredDate,
}) => {
  const featureName = FeatureEnumToNameMap[featureEnum];
  const trialPeriodDuration = getFeatureTrialDuration(featureEnum);
  const { modalProps: startTrialModalProps, openModal: openStartTrialModal } =
    useModalControl();

  const infoText = useMemo(() => {
    switch (addonStatus) {
      case SubscriptionAddOnStatus.FREE_TRIAL_ACTIVE:
        return `${daysLeftInTrial} days remaining (trial ends on ${trialEndDate})`;
      case SubscriptionAddOnStatus.FREE_TRIAL_ELIGIBLE:
        return `${trialPeriodDuration}-day trial period`;
      case SubscriptionAddOnStatus.ADDON_STATUS_UNSPECIFIED:
        return trialExpiredDate ? `Free Trial completed on ${trialExpiredDate}` : '';
      default:
        return '';
    }
  }, [addonStatus, daysLeftInTrial, trialEndDate, trialExpiredDate, trialPeriodDuration]);

  return (
    <div css={styles.card}>
      <div css={styles.headerRow}>
        <Heading level={3} css={{ margin: 0 }}>
          {featureName ?? featureEnum}
        </Heading>

        {addonStatus === SubscriptionAddOnStatus.ACTIVE && (
          <Chip variant="success" css={styles.activeChip}>
            Active
          </Chip>
        )}

        {addonStatus === SubscriptionAddOnStatus.FREE_TRIAL_ACTIVE && (
          <Chip variant="success" css={styles.activeChip}>
            Trial Active
          </Chip>
        )}

        {addonStatus === SubscriptionAddOnStatus.ADDON_STATUS_UNSPECIFIED && (
          <Chip variant="critical" css={styles.alertChip}>
            Unavailable
          </Chip>
        )}
        {addonStatus === SubscriptionAddOnStatus.FREE_TRIAL_ACTIVE &&
          daysLeftInTrial >= 0 &&
          daysLeftInTrial <= ENDING_SOON_THRESHOLD_DAYS && (
            <Chip variant="warn" css={styles.warningChip}>
              Ending Soon
            </Chip>
          )}
      </div>

      {infoText && (
        <div css={styles.infoRow}>
          {addonStatus === SubscriptionAddOnStatus.ADDON_STATUS_UNSPECIFIED ? (
            <BlockIconSmall color="light" />
          ) : (
            <ClockIconSmall color="light" />
          )}
          <Text size="medium" color="light" css={styles.infoText}>
            {infoText}
          </Text>
        </div>
      )}

      {addonStatus === SubscriptionAddOnStatus.FREE_TRIAL_ELIGIBLE && (
        <div css={styles.actionRow}>
          <PrimaryButton css={{ maxWidth: 'fit-content' }} onClick={openStartTrialModal}>
            Start Trial
          </PrimaryButton>
        </div>
      )}

      <StartTrialModal
        {...startTrialModalProps}
        featureEnum={featureEnum}
        addonEnum={addonEnum}
      />
    </div>
  );
};

const styles = {
  card: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    border: 1px solid ${theme.colors.gray300};
    border-radius: ${theme.borderRadius.medium};
    max-width: 511px;
  `,
  headerRow: css`
    display: flex;
    align-items: center;
    gap: 16px;
  `,
  infoRow: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  actionRow: css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
  `,
  infoText: css`
    margin: 0;
  `,
  warningChip: css`
    && {
      background-color: #fff1d6;
      color: #7a4900;
    }
  `,
  activeChip: css`
    && {
      background-color: #c5f6da;
      color: #0c6734;
    }
  `,
  alertChip: css`
    && {
      background-color: #ffe3e0;
      color: #bd0f0f;
    }
  `,
};
